import { useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { Field, NumberInput, FileUploader } from "../common";
import { useLang } from "../../lib/i18n";
import { SERVICE_TYPES, QTY_FIELDS, emptyComponent } from "../../lib/labels";
import { api } from "../../lib/api";

let zonesPromise = null;
function useZones() {
  const [zones, setZones] = useState({});
  useEffect(() => {
    zonesPromise = zonesPromise || api.get("/tariff-zones").then((r) => r.data).catch(() => ({}));
    zonesPromise.then(setZones);
  }, []);
  return zones;
}

function ComponentEditor({ comp, onChange, onRemove, pi, ci, mixed }) {
  const { tr } = useLang();
  const set = (patch) => onChange({ ...comp, ...patch });
  const setQty = (k, v) => set({ qty: { ...comp.qty, [k]: v } });
  const t = `p${pi}-c${ci}`;
  const zones = useZones()[comp.type] || [];
  const q = comp.qty;
  const palWarn = (p, s) => q[p] !== "" && q[s] !== "" && Number(q[s]) > Number(q[p]);
  return (
    <div className="border border-slate-200 rounded-xl p-3 space-y-3 bg-slate-50/60" data-testid={`component-${t}`}>
      <div className="flex gap-2 items-end">
        <Field label={mixed ? `Composante ${ci + 1}` : "Type de prestation"} className="flex-1">
          <select className="tl-select" value={comp.type} onChange={(e) => set({ type: e.target.value })} data-testid={`type-select-${t}`}>
            {Object.keys(SERVICE_TYPES).filter((k) => k !== "express" || comp.type === "express").map((k) => <option key={k} value={k}>{tr(SERVICE_TYPES[k])}</option>)}
          </select>
        </Field>
        {onRemove && (
          <button type="button" className="tl-btn tl-btn-danger" onClick={onRemove} aria-label="Supprimer la composante" data-testid={`remove-component-${t}`}>
            <Trash2 size={18} />
          </button>
        )}
      </div>
      {zones.length > 0 && !["special", "special_fret"].includes(comp.type) && (
        <Field label="Destination tarifée">
          <select className="tl-select" value={comp.zone || ""} onChange={(e) => set({ zone: e.target.value })} data-testid={`zone-select-${t}`}>
            <option value="">{comp.type === "special" ? "Spécial général (autre destination)" : "— Choisir la destination —"}</option>
            {zones.map((z) => <option key={z} value={z}>{z}</option>)}
          </select>
        </Field>
      )}
      {!["special", "special_fret"].includes(comp.type) && (<>
      <div className="grid grid-cols-2 gap-3">
        {QTY_FIELDS.map(([k, label]) => (
          <Field key={k} label={label}>
            <NumberInput value={q[k]} onChange={(v) => setQty(k, v)} testid={`qty-${k}-${t}`} />
          </Field>
        ))}
      </div>
      {palWarn("del_pallets", "del_pallet_stops") && <p className="text-sm text-red-700" data-testid={`pallet-warning-${t}`}>Stops palettes livrées supérieurs au nombre de palettes livrées : vérifiez.</p>}
      {palWarn("pu_pallets", "pu_pallet_stops") && <p className="text-sm text-red-700">Stops palettes enlevées supérieurs au nombre de palettes enlevées : vérifiez.</p>}
      <p className="text-xs text-slate-500">Laissez vide si l'information est inconnue ; saisissez 0 si la quantité est nulle. Plusieurs palettes au même stop : 1 stop palette, le reste compte comme palettes supplémentaires.</p>
      </>)}
      {["special", "special_fret"].includes(comp.type) && <SpecialFields comp={comp} set={set} zones={comp.type === "special" ? zones : []} t={t} fret={comp.type === "special_fret"} />}
      {comp.type === "express_plus" && comp.zone.toLowerCase() === "hors bruxelles" && <KmTime comp={comp} set={set} t={t} />}
    </div>
  );
}

const toMin = (s) => (s ? Number(s.slice(0, 2)) * 60 + Number(s.slice(3, 5)) : null);

const SP_QTY = {
  livraison: { colis: [["del_pckgs", "Colis livrés"], ["del_stops", "Stops livrés"]], palette: [["del_pallets", "Palettes livrées"], ["del_pallet_stops", "Stops palettes livrées"]] },
  enlevement: { colis: [["pu_pckgs", "Colis enlevés"], ["pu_stops", "Stops enlevés"]], palette: [["pu_pallets", "Palettes enlevées"], ["pu_pallet_stops", "Stops palettes enlevées"]] },
};

function Choice({ value, options, onChange, testid }) {
  return (
    <div className="col-span-2 grid grid-cols-2 gap-2">
      {options.map(([k, l]) => (
        <button key={k} type="button" onClick={() => onChange(k)} className={`tl-btn ${value === k ? "tl-btn-dark" : "tl-btn-ghost"}`} data-testid={`${testid}-${k}`}>{l}</button>
      ))}
    </div>
  );
}

function KmTime({ comp, set, t }) {
  const sp = comp.special;
  const setSp = (patch) => set({ special: { ...sp, ...patch } });
  return (
    <div className="grid grid-cols-2 gap-3 border-t border-dashed border-slate-300 pt-3" data-testid={`express-km-${t}`}>
      <p className="col-span-2 text-xs text-amber-700">Express+ hors Bruxelles : kilométrage et durée nécessaires au calcul.</p>
      <Field label="Kilométrage aller-retour" className="col-span-2"><NumberInput step="0.1" value={sp.km} onChange={(v) => setSp({ km: v })} testid={`special-km-${t}`} /></Field>
      <Field label="Durée — heures"><NumberInput value={sp.hours} onChange={(v) => setSp({ hours: v })} testid={`special-hours-${t}`} /></Field>
      <Field label="Durée — minutes"><NumberInput value={sp.minutes} onChange={(v) => setSp({ minutes: v })} testid={`special-minutes-${t}`} /></Field>
    </div>
  );
}

function SpecialFields({ comp, set, zones, t, fret }) {
  const sp = comp.special;
  const setSp = (patch) => set({ special: { ...sp, ...patch } });
  const pickDest = (v) => {
    const z = zones.find((x) => x.toLowerCase() === v.trim().toLowerCase());
    set({ zone: z || "", special: { ...sp, destination: v } });
  };
  const choose = (kind, nature) => {
    const keep = (SP_QTY[kind]?.[nature] || []).map(([k]) => k);
    const qty = Object.fromEntries(QTY_FIELDS.map(([k]) => [k, keep.includes(k) ? comp.qty[k] : 0]));
    set({ qty, special: { ...sp, kind, nature } });
  };
  const setTime = (k, v) => {
    const next = { ...sp, [k]: v };
    const a = toMin(next.start), b = toMin(next.end);
    if (a !== null && b !== null) {
      const d = (b - a + 1440) % 1440;
      Object.assign(next, { hours: Math.floor(d / 60), minutes: d % 60 });
    }
    setSp(next);
  };
  const fields = SP_QTY[sp.kind]?.[sp.nature] || [];
  return (
    <div className="grid grid-cols-2 gap-3 border-t border-dashed border-slate-300 pt-3" data-testid={`special-fields-${t}`}>
      <span className="tl-label col-span-2">1. Livraison ou enlèvement</span>
      <Choice value={sp.kind} options={[["livraison", "Livraison"], ["enlevement", "Enlèvement"]]} onChange={(k) => choose(k, fret ? "palette" : sp.nature || "colis")} testid={`special-kind-${t}`} />
      {sp.kind && (<>
        {!fret && <span className="tl-label col-span-2">2. Colis ou palette</span>}
        {!fret && <Choice value={sp.nature} options={[["colis", "Colis"], ["palette", "Palette"]]} onChange={(n) => choose(sp.kind, n)} testid={`special-nature-${t}`} />}
        {fields.map(([k, l]) => (
          <Field key={k} label={l}><NumberInput value={comp.qty[k]} onChange={(v) => set({ qty: { ...comp.qty, [k]: v } })} testid={`qty-${k}-${t}`} /></Field>
        ))}
        <Field label="Lieu de départ" className="col-span-2"><input className="tl-input" value={sp.origin || ""} onChange={(e) => setSp({ origin: e.target.value })} data-testid={`special-origin-${t}`} /></Field>
        <Field label="Lieu d'arrivée (tapez pour chercher)" className="col-span-2">
          <input className="tl-input" list={`dest-${t}`} value={sp.destination} onChange={(e) => pickDest(e.target.value)} data-testid={`special-destination-${t}`} />
          <datalist id={`dest-${t}`}>{zones.map((z) => <option key={z} value={z} />)}</datalist>
        </Field>
        <p className={`col-span-2 text-xs font-semibold ${comp.zone ? "text-emerald-700" : "text-amber-700"}`} data-testid={comp.zone ? `special-fixed-${t}` : `special-general-${t}`}>
          {fret ? "Spécial frêt : forfait + palettes + km + heures supplémentaires." : comp.zone ? "Destination à prix fixe : le montant est appliqué automatiquement." : "Autre destination : tarif spécial général (forfait + km + heures)."}
        </p>
        {!comp.zone && <Field label="Adresse d'arrivée" className="col-span-2"><input className="tl-input" value={sp.address} onChange={(e) => setSp({ address: e.target.value })} data-testid={`special-address-${t}`} /></Field>}
        <Field label="Kilométrage aller-retour" className="col-span-2"><NumberInput step="0.1" value={sp.km} onChange={(v) => setSp({ km: v })} testid={`special-km-${t}`} /></Field>
        <Field label="Heure aller (départ)"><input type="time" className="tl-input" value={sp.start || ""} onChange={(e) => setTime("start", e.target.value)} data-testid={`special-start-${t}`} /></Field>
        <Field label="Heure retour"><input type="time" className="tl-input" value={sp.end || ""} onChange={(e) => setTime("end", e.target.value)} data-testid={`special-end-${t}`} /></Field>
        <Field label="Durée — heures"><NumberInput value={sp.hours} onChange={(v) => setSp({ hours: v })} testid={`special-hours-${t}`} /></Field>
        <Field label="Durée — minutes"><NumberInput value={sp.minutes} onChange={(v) => setSp({ minutes: v })} testid={`special-minutes-${t}`} /></Field>
        <div className="col-span-2"><FileUploader kind="piece" accept="image/*,.pdf" files={sp.photos || []} onChange={(v) => setSp({ photos: v })} label="Photo (si nécessaire)" testid={`special-photo-${t}`} /></div>
        <Field label="Remarques (si nécessaire)" className="col-span-2"><textarea className="tl-textarea" value={sp.remarks || ""} onChange={(e) => setSp({ remarks: e.target.value })} data-testid={`special-remarks-${t}`} /></Field>
      </>)}
    </div>
  );
}

export default function PrestationEditor({ p, pi, onChange, onRemove }) {
  const set = (patch) => onChange({ ...p, ...patch });
  const setComp = (ci, c) => set({ components: p.components.map((x, i) => (i === ci ? c : x)) });
  return (
    <div className="tl-card space-y-3 tl-fade" data-testid={`prestation-${pi}`}>
      <div className="flex items-center gap-2">
        <h3 className="text-2xl font-bold uppercase">Prestation {pi + 1}</h3>
        {onRemove && <button type="button" className="ml-auto tl-btn tl-btn-danger tl-btn-sm" onClick={onRemove} data-testid={`remove-prestation-${pi}`}><Trash2 size={16} /> Retirer</button>}
      </div>
      <div className="grid grid-cols-2 gap-2" role="radiogroup">
        {[[false, "Prestation simple"], [true, "Prestation mixte"]].map(([v, l]) => (
          <button key={l} type="button" onClick={() => set({ mixed: v, components: v ? p.components : [p.components[0]] })}
            className={`tl-btn ${p.mixed === v ? "tl-btn-dark" : "tl-btn-ghost"}`} data-testid={`mixed-${v ? "yes" : "no"}-${pi}`}>{l}</button>
        ))}
      </div>
      {p.mixed && <p className="text-xs text-slate-600">Une prestation mixte reste sur une seule ligne. Répartissez les quantités entre composantes sans compter deux fois le même stop.</p>}
      {p.components.map((c, ci) => (
        <ComponentEditor key={ci} comp={c} pi={pi} ci={ci} mixed={p.mixed} onChange={(nc) => setComp(ci, nc)}
          onRemove={p.mixed && p.components.length > 1 ? () => set({ components: p.components.filter((_, i) => i !== ci) }) : null} />
      ))}
      {p.mixed && (
        <button type="button" className="tl-btn tl-btn-ghost w-full" onClick={() => set({ components: [...p.components, emptyComponent("special")] })} data-testid={`add-component-${pi}`}>
          <Plus size={18} /> Ajouter une composante
        </button>
      )}
      <Field label="Observations de la prestation">
        <textarea className="tl-textarea" value={p.observations} onChange={(e) => set({ observations: e.target.value })} data-testid={`prestation-observations-${pi}`} />
      </Field>
    </div>
  );
}
